import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TabContext from "@mui/lab/TabContext";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Icon } from "@iconify/react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Layout from "../../../components/Layout";
import Button from "../../../components/Button";
import BreadCrumb from "../../../components/BreadCrumb";
import useStyles from "../styles";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import MaterialPreviewModal from "../components/MaterialPreviewModal";
import { formatFileSize, getFileMeta } from "../employer/utils/uploadFile";

const SYLLABUS_ENDPOINT = "/api/v1/syllabus";
const SUBJECTS_ENDPOINT = "/api/v1/subjects";

const getProgressMeta = (progress) => {
  if (progress === "COMPLETED") return { label: "Completed", background: "#12B76A", color: "#fff", pillClass: "progressCompleted" };
  if (progress && progress !== "NOT_STARTED") return { label: "In Progress", background: "#155EEF", color: "#fff", pillClass: "progressInProgress" };

  return { label: "Not Started", background: "#F2F4F7", color: "#475467", pillClass: "progressNotStarted" };
};

const EmployeeSyllabusDetails = () => {
  const { subjectId, syllabusId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();

  const [subjectName, setSubjectName] = useState("");

  const [syllabus, setSyllabus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState("syllabus");
  const [materials, setMaterials] = useState([]);
  const [isMaterialsLoading, setIsMaterialsLoading] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [previewMaterial, setPreviewMaterial] = useState(null);

  const getSubject = () => {
    httprequest.get(`${SUBJECTS_ENDPOINT}/${subjectId}`)
      .then((res) => setSubjectName(res?.data?.data?.name))
      .catch(() => {});
  };

  const resolveSyllabus = () => {
    setIsLoading(true);
    if (!syllabusId) {
      setSyllabus(null);
      setIsLoading(false);

      return;
    }
    httprequest.get(`${SYLLABUS_ENDPOINT}/${syllabusId}`)
      .then((detail) => {
        const data = detail?.data?.data;
        setSyllabus(data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        setSyllabus(null);
        toast.error(err?.response?.data?.message);
      });
  };

  const getMaterials = () => {
    if (!syllabus?.id) return;
    setIsMaterialsLoading(true);
    httprequest.get(`${SYLLABUS_ENDPOINT}/${syllabus.id}/materials`)
      .then((res) => {
        setMaterials(res?.data?.data || []);
        setIsMaterialsLoading(false);
      })
      .catch((err) => {
        setIsMaterialsLoading(false);
        toast.error(err?.response?.data?.message);
      });
  };

  useEffect(() => {
    getSubject();
    resolveSyllabus();
  }, [subjectId]);

  useEffect(() => {
    if (tab === "materials") getMaterials();
  }, [tab, syllabus?.id]);

  const classNames = syllabus?.classes?.map((c) => c?.class_name) || [];
  const classSummary = classNames.length > 2 ? `${classNames.slice(0, 2).join(", ")}, +${classNames.length - 2}` : classNames.join(", ");

  const topicRows = (syllabus?.weeks || []).flatMap((week) => (week?.topics || []).map((topic) => ({
    ...topic,
    weekNumber: week?.week,
    weekTitle: week?.title,
    weekTopicsCount: week?.topics?.length || 0
  })));

  const handleDownloadSyllabus = () => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(syllabus?.title || subjectName, 14, 18);
    doc.setFontSize(11);
    doc.text(`${syllabus?.session?.name || ""} Session - ${syllabus?.term?.name || ""}`, 14, 26);

    autoTable(doc, {
      startY: 34,
      head: [["Week", "Topic", "Status"]],
      body: topicRows.map((topic) => [`Week ${topic?.weekNumber}`, topic?.title, getProgressMeta(topic?.progress).label]),
      headStyles: { fillColor: [21, 94, 239] },
      styles: { cellPadding: 4, fontSize: 10 }
    });

    doc.save(`${subjectName || "Syllabus"}.pdf`);
  };

  return (
    <Layout
      children={
        <>
          <BreadCrumb
            link_one={`/class-activities/employee/syllabus/${subjectId}`}
            link_one_name="Syllabus Details"
            active="Syllabus Content"
          />

          {!isLoading && !syllabus && (
            <div className={`${classes.emptyStateContainer} page-content`} style={{ marginTop: "20px" }}>
              <div className={classes.emptyStateIcon}><Icon icon="ph:book-open-text-light" /></div>
              <h3 className={classes.emptyStateTitle}>No Syllabus Yet</h3>
              <p className={classes.emptyStateSubtitle}>
                A syllabus hasn&apos;t been published for this subject yet. In the meantime, you can
                upload class materials.
              </p>
            </div>
          )}

          {!isLoading && syllabus && (
            <div style={{ marginTop: "20px" }}>
              <div className={`${classes.editorHeader} white_bg`}>
                <div style={{ display: "flex", gap: 12 }}>
                  <div className={classes.weekBadge} style={{ width: 44, height: 44, borderRadius: 12, fontSize: 15 }}>
                    {syllabus?.title?.slice(0, 2)?.toUpperCase()}
                  </div>
                  <div style={{ textAlign: "left" }}>
                    <h4 style={{ margin: 0 }}>{syllabus?.title}</h4>
                    <div className={classes.editorHeaderInfo} style={{ marginTop: 10 }}>
                      <span className={classes.metaChip}>
                        <Icon icon="mdi:calendar-blank-outline" /> {syllabus?.session?.name} Session
                      </span>
                      <span className={classes.metaChip}>{syllabus?.term?.name}</span>
                      <span className={classes.metaChip}>
                        <Icon icon="mdi:google-classroom" /> {classNames.length} classes ({classSummary})
                        <Icon icon="mdi:chevron-down" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <TabContext value={tab}>
                <div className="tablist-container padding-left-16">
                  <Tabs className="overide-tablist" onChange={(e, v) => setTab(v)} aria-label="syllabus tabs">
                    <Tab label="Syllabus" value="syllabus" />
                    <Tab label="Materials" value="materials" />
                  </Tabs>
                </div>

                <TabPanel value="syllabus" className="tabpanel-overide">
                  <div className="white_bg">
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
                      <div style={{ marginLeft: "auto" }}>
                        <Button
                          buttonName="Download Syllabus"
                          buttonIcon={<Icon icon="mdi:tray-arrow-down" style={{ marginRight: 6 }} />}
                          variant="primaryoutline"
                          color="btnfontprimary"
                          buttonSize="fluid"
                          onClick={handleDownloadSyllabus}
                        />
                      </div>
                    </div>

                    {(syllabus?.weeks || []).map((week) => {
                      const meta = getProgressMeta(week?.progress);
                      const isExpanded = expandedWeeks[week.id];

                      return (
                        <div key={week.id} style={{ border: "1px solid #EAECF0", borderRadius: 10, marginBottom: 16, overflow: "hidden", background: "white" }}>
                          <div
                            style={{ padding: "14px 16px", display: "flex", alignItems: "center", cursor: "pointer", gap: 12 }}
                            onClick={() => setExpandedWeeks((prev) => ({ ...prev, [week.id]: !prev[week.id] }))}
                          >
                            <span className={classes.weekBadge} style={{ background: meta.background, color: meta.color, flexShrink: 0 }}>
                              W{week.week}
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p className={classes.topicTitle}>{week.title}</p>
                              <p className={classes.weekItemSubtitle}>Week {week.week}, {week.topics?.length || 0} Topic{(week.topics?.length === 1) ? "" : "s"}</p>
                            </div>
                            <div className={classes.topicActions}>
                              <span className={classes.progressBadge} style={{ background: meta.pillClass === "progressCompleted" ? "#ECFDF3" : meta.pillClass === "progressInProgress" ? "#EFF8FF" : "#F2F4F7", color: meta.pillClass === "progressCompleted" ? "#027A48" : meta.pillClass === "progressInProgress" ? "#175CD3" : "#475467" }}>
                                {meta.label}
                              </span>
                              <Icon icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} style={{ color: "#667085", fontSize: 20 }} />
                            </div>
                          </div>

                          {isExpanded && week.topics && week.topics.length > 0 && (
                            <div style={{ padding: "0 16px 20px 72px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12, textAlign: "left" }}>
                              {week.topics.map((topic) => (
                                <div
                                  key={topic.id}
                                  style={{ cursor: "pointer", color: "#155EEF", fontSize: 14, fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3, textAlign: "left" }}
                                  onClick={() => navigate(`/class-activities/employee/syllabus/${subjectId}/${syllabus?.id}/topics/${topic?.id}`)}
                                >
                                  {topic.title}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TabPanel>

                <TabPanel value="materials" className="tabpanel-overide">
                  {materials.length === 0 && !isMaterialsLoading ? (
                    <div className={classes.emptyStateContainer}>
                      <div className={classes.emptyStateIcon}><Icon icon="mdi:folder-outline" /></div>
                      <h3 className={classes.emptyStateTitle}>No materials yet</h3>
                      <p className={classes.emptyStateSubtitle}>Materials uploaded for this subject will appear here.</p>
                    </div>
                  ) : (
                    <div className={classes.materialsGrid}>
                      {materials.map((material) => {
                        const meta = getFileMeta(material?.file_name);

                        return (
                          <div key={material?.id} className={classes.materialCard} onClick={() => setPreviewMaterial(material)}>
                            <div className={classes.materialIcon} style={{ background: meta.bg, color: meta.color }}>
                              <Icon icon={meta.icon} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <p className={classes.materialTitle}>{material?.title}</p>
                              <p className={classes.materialMeta}>{meta.label} . {formatFileSize(material?.file_size)}</p>
                            </div>
                            <div className={classes.materialActions}>
                              <Icon icon="mdi:download-outline" onClick={(e) => {
                                e.stopPropagation();
                                window.open(material?.file_url, "_blank");
                              }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabPanel>
              </TabContext>
            </div>
          )}

          <MaterialPreviewModal
            isOpen={!!previewMaterial}
            closeModal={() => setPreviewMaterial(null)}
            material={previewMaterial}
          />
        </>
      }
    />
  );
};

export default EmployeeSyllabusDetails;
